/* ===== Dues: record a manual payment ===== */

import { useMemo, useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import {
  MAX_DUE_MONTHS_PER_PAYMENT,
  formatPeriodLabel,
  isDuePeriod,
  periodRange,
  type DuesRosterEntry,
  type ManualDuePaymentMethod,
  type OrgDuesSettings,
} from '@zetyar/domain'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDuesRepository } from '@/infrastructure/repositories/dues'
import { FIELD_CLASS, PAYMENT_METHOD_LABELS, duesErrorMessage, formatMoney } from './duesFormat'

interface RecordPaymentModalProps {
  readonly context: TenantContext
  readonly entry: DuesRosterEntry
  readonly initialPeriod: string
  readonly settings: OrgDuesSettings
  readonly onClose: () => void
  readonly onRecorded: () => void
}

// Mercado Pago is deliberately not offered: online payments are only confirmed by the webhook.
const METHODS: ManualDuePaymentMethod[] = ['transfer', 'cash', 'other']
const MONTH_OPTIONS = Array.from({ length: MAX_DUE_MONTHS_PER_PAYMENT }, (_, index) => index + 1)

export function RecordPaymentModal({ context, entry, initialPeriod, settings, onClose, onRecorded }: RecordPaymentModalProps) {
  const [startPeriod, setStartPeriod] = useState(initialPeriod)
  const [months, setMonths] = useState(1)
  const [method, setMethod] = useState<ManualDuePaymentMethod>('transfer')
  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const periods = useMemo(() => (isDuePeriod(startPeriod) ? periodRange(startPeriod, months) : []), [startPeriod, months])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const recordPayment = webDuesRepository.recordPayment
    if (!recordPayment || periods.length === 0 || isSaving) return

    setIsSaving(true)
    setError(null)
    try {
      await recordPayment(context, { membershipId: entry.membershipId, startPeriod, months, method, notes })
      onRecorded()
    } catch (caught) {
      console.error('Error al registrar el pago de cuota:', caught)
      setError(duesErrorMessage(caught))
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isSaving) onClose()
      }}
      title={`Registrar pago - ${entry.displayName}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type="submit" form="record-due-payment-form" isLoading={isSaving} disabled={periods.length === 0}>
            Confirmar pago
          </Button>
        </>
      }
    >
      <form id="record-due-payment-form" className="space-y-4" onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Desde el mes
            <input type="month" required value={startPeriod} onChange={(event) => setStartPeriod(event.target.value)} className={FIELD_CLASS} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Cantidad de meses
            <select value={months} onChange={(event) => setMonths(Number(event.target.value))} className={FIELD_CLASS}>
              {MONTH_OPTIONS.map((count) => (
                <option key={count} value={count}>
                  {count} {count === 1 ? 'mes' : 'meses'}
                </option>
              ))}
            </select>
          </label>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">Método de pago</legend>
          <div className="grid grid-cols-3 gap-2">
            {METHODS.map((value) => (
              <label
                key={value}
                className={`flex cursor-pointer items-center justify-center rounded-[13px] border px-3 py-2 text-xs font-semibold has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 ${
                  method === value
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                <input
                  type="radio"
                  name="due-payment-method"
                  value={value}
                  checked={method === value}
                  onChange={() => setMethod(value)}
                  className="sr-only"
                />
                {PAYMENT_METHOD_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>

        <Input label="Notas (opcional)" placeholder="Ej: N° de comprobante" maxLength={500} value={notes} onChange={(event) => setNotes(event.target.value)} />

        <div className="flex items-start justify-between gap-3 rounded-[13px] bg-neutral-50 p-4 dark:bg-neutral-800/60">
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Meses que cubre</p>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {periods.length > 0 ? periods.map((period) => formatPeriodLabel(period)).join(', ') : 'Selecciona un mes válido'}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Total</p>
            <p className="text-lg font-bold text-primary-600 dark:text-primary-300">
              {formatMoney(settings.monthlyAmount * months, settings.currency)}
            </p>
          </div>
        </div>

        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Se registrará un único ingreso en Finanzas por el total. Si hay un error, podrás revertir el pago completo.
        </p>
        {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}
      </form>
    </Modal>
  )
}
